import { MouseEvent, useState } from 'react';
import { IconButton, TableCell, TableHead, TableRow } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowDropDownCircleOutlinedIcon from '@mui/icons-material/ArrowDropDownCircleOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { ReactComponent as CourseMappingCreditsHeaderIcon } from '../assets/course-mapping-credits-header-icon.svg';
import { ReactComponent as CourseMappingCheckboxHeaderIcon } from '../assets/course-mapping-checkbox-header-icon.svg';
import { CourseMappingCellType, CourseMappingColumn } from '../../interfaces/CourseMappingInterface';
import { CourseMappingSortOrder, CourseMappingSortState } from './courseMappingSort';
import { buildColumnLayout } from './courseMappingColumnVisibility';
import CourseMappingColumnMenu from './CourseMappingColumnMenu';
import CourseMappingHiddenColumnsMenu from './CourseMappingHiddenColumnsMenu';

const HEADER_ICONS: Record<CourseMappingCellType, React.ReactNode> = {
  locked: <LockOutlinedIcon fontSize="inherit" />,
  dropdown: <ArrowDropDownCircleOutlinedIcon fontSize="inherit" />,
  editable: <CourseMappingCreditsHeaderIcon className="course-mapping-credits-header-icon" />,
  checkbox: <CourseMappingCheckboxHeaderIcon className="course-mapping-checkbox-header-icon" />,
};

interface CourseMappingTableHeadProps {
  sort: CourseMappingSortState | null;
  hiddenColumns: Set<string>;
  onSortChange: (next: CourseMappingSortState | null) => void;
  onOpenFilter: (columnKey: string) => void;
  onHideColumn: (columnKey: string) => void;
  onShowColumn: (columnKey: string) => void;
}

const CourseMappingTableHead = ({
  sort,
  hiddenColumns,
  onSortChange,
  onOpenFilter,
  onHideColumn,
  onShowColumn,
}: CourseMappingTableHeadProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [menuColumn, setMenuColumn] = useState<CourseMappingColumn | null>(null);
  const [revealAnchor, setRevealAnchor] = useState<HTMLElement | null>(null);
  const [revealColumns, setRevealColumns] = useState<CourseMappingColumn[]>([]);

  const { visible, hiddenBefore, hiddenTrailing } = buildColumnLayout(hiddenColumns);

  const handleOpenMenu = (event: MouseEvent<HTMLButtonElement>, column: CourseMappingColumn) => {
    setAnchorEl(event.currentTarget);
    setMenuColumn(column);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuColumn(null);
  };

  const handleOpenReveal = (event: MouseEvent<HTMLButtonElement>, columns: CourseMappingColumn[]) => {
    setRevealAnchor(event.currentTarget);
    setRevealColumns(columns);
  };

  const handleCloseReveal = () => {
    setRevealAnchor(null);
    setRevealColumns([]);
  };

  const handleSort = (order: CourseMappingSortOrder | null) => {
    if (menuColumn) {
      onSortChange(order === null ? null : { key: menuColumn.key, order });
    }
  };

  const handleFilter = () => {
    if (menuColumn) {
      onOpenFilter(menuColumn.key);
    }
  };

  const handleHide = () => {
    if (menuColumn) {
      onHideColumn(menuColumn.key);
    }
  };

  const renderRevealMarker = (columns: CourseMappingColumn[], variant: 'before' | 'trailing') => (
    <button
      type="button"
      className={`course-mapping-reveal-marker course-mapping-reveal-marker--${variant}`}
      aria-label={`Show hidden ${columns.length === 1 ? 'column' : 'columns'}: ${columns
        .map((column) => column.label)
        .join(', ')}`}
      aria-haspopup="menu"
      onClick={(event) => handleOpenReveal(event, columns)}
    >
      <KeyboardArrowLeftIcon fontSize="inherit" />
      <KeyboardArrowRightIcon fontSize="inherit" />
    </button>
  );

  return (
    <TableHead>
      <TableRow className="course-mapping-header-row">
        <TableCell className="course-mapping-row-number-cell course-mapping-header-cell" />
        <TableCell className="course-mapping-expand-cell course-mapping-header-cell course-mapping-expand-cell--no-divider" />
        {visible.map((column, columnIndex) => {
          const isSortedBy = sort?.key === column.key;
          const isMenuOpen = menuColumn?.key === column.key;
          const isLastVisible = columnIndex === visible.length - 1;
          const hiddenInGap = hiddenBefore[column.key];

          return (
            <TableCell
              key={column.key}
              className={`course-mapping-header-cell course-mapping-data-cell${
                columnIndex === 0 ? ' course-mapping-data-cell--first' : ''
              }${column.cellType === 'checkbox' ? ' course-mapping-data-cell--checkbox' : ''}`}
            >
              {hiddenInGap ? renderRevealMarker(hiddenInGap, 'before') : null}
              {isLastVisible && hiddenTrailing.length > 0 ? renderRevealMarker(hiddenTrailing, 'trailing') : null}
              <span className="course-mapping-header-content">
                <span className="course-mapping-header-icon">{HEADER_ICONS[column.cellType]}</span>
                <span className="course-mapping-header-label">{column.label}</span>
                <IconButton
                  size="small"
                  className={`course-mapping-header-menu-button${
                    isMenuOpen || isSortedBy ? ' course-mapping-header-menu-button--visible' : ''
                  }`}
                  aria-label={`Open ${column.label} column menu`}
                  aria-haspopup="menu"
                  aria-expanded={isMenuOpen}
                  onClick={(event) => handleOpenMenu(event, column)}
                >
                  {isSortedBy ? (
                    sort?.order === 'asc' ? (
                      <ArrowDownwardIcon fontSize="small" />
                    ) : (
                      <ArrowUpwardIcon fontSize="small" />
                    )
                  ) : (
                    <ArrowDropDownIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </TableCell>
          );
        })}
      </TableRow>
      {menuColumn ? (
        <CourseMappingColumnMenu
          column={menuColumn}
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          sort={sort}
          onClose={handleCloseMenu}
          onSort={handleSort}
          onFilter={handleFilter}
          onHide={handleHide}
        />
      ) : null}
      {revealColumns.length > 0 ? (
        <CourseMappingHiddenColumnsMenu
          columns={revealColumns}
          anchorEl={revealAnchor}
          open={Boolean(revealAnchor)}
          onClose={handleCloseReveal}
          onShowColumn={onShowColumn}
        />
      ) : null}
    </TableHead>
  );
};

export default CourseMappingTableHead;
